import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { ShieldCheck, CheckCircle2, XCircle, Sparkles, AlertTriangle, Loader2, Trash2, Smartphone } from "lucide-react";
import { toast } from "sonner";

const StatusDot = ({ ok }) =>
  ok ? <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0" />
     : <XCircle className="h-4 w-4 text-red-500 shrink-0" />;

/**
 * Vertrauens-Check Panel + Vertrauenssystem-Statusbox.
 */
export default function TrustCheckPanel({ onChanged }) {
  const { t, lang } = useI18n();
  const [status, setStatus] = useState(null);
  const [last, setLast] = useState(null);   // letztes Check-Ergebnis (Modal-Inhalt)
  const [running, setRunning] = useState(false);
  const [clearing, setClearing] = useState(false);

  const loadStatus = async () => {
    try {
      const { data } = await api.get("/trust-card/system-status");
      setStatus(data);
    } catch (e) { /* ignore */ }
  };

  useEffect(() => { loadStatus(); }, []);

  const runCheck = async () => {
    setRunning(true);
    try {
      const { data } = await api.post("/trust-card/trust-check", {});
      setLast(data);
      if (data.ok) toast.success(t.trustCard.trustCheck.successTitle);
      else toast.warning(t.trustCard.trustCheck.failTitle);
      await loadStatus();
      if (onChanged) onChanged();
    } catch (e) {
      toast.error("Vertrauens-Check konnte nicht gestartet werden");
    } finally {
      setRunning(false);
    }
  };

  const clearTestEntries = async () => {
    setClearing(true);
    try {
      const { data } = await api.delete("/trust-card/trust-check/test-entries");
      toast.success(`${data.deleted} Test-Einträge entfernt.`);
      if (onChanged) onChanged();
    } catch (e) {
      toast.error("Konnte Testeinträge nicht entfernen");
    } finally {
      setClearing(false);
    }
  };

  const StatusDotInner = StatusDot; // alias kept for inline use
  return (
    <section className="grid lg:grid-cols-[1.2fr_1fr] gap-6 mb-12" data-testid="trust-check-section">
      {/* System status box */}
      <div className="ag-card p-7" data-testid="trust-system-box">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className={`h-2.5 w-2.5 rounded-full ${status?.overall_ok ? "bg-emerald-500" : "bg-amber-500"}`} />
              <h2 className="font-serif text-2xl text-ag-ink">{t.trustCard.systemBox.title}</h2>
            </div>
            <div className="text-xs text-slate-500 mt-1">
              {status?.is_demo ? t.trustCard.trustCheck.demoOnly : t.trustCard.trustCheck.liveOnly}
            </div>
          </div>
          <span className={`ag-badge ${status?.overall_ok ? "ag-badge-green" : "ag-badge-amber"}`}>
            {status?.overall_ok ? t.trustCard.trustCheck.ok : t.trustCard.trustCheck.notOk}
          </span>
        </div>

        <ul className="mt-5 space-y-2.5">
          {(status?.items || []).map((it) => (
            <li key={it.key} className="flex items-center gap-2.5 text-sm" data-testid={`status-${it.key}`}>
              <StatusDot ok={it.ok} />
              <span className="text-slate-700">{t.trustCard.systemBox.items[it.key] || it.title_de}</span>
            </li>
          ))}
        </ul>

        <div className="mt-5 pt-5 border-t border-slate-100 flex items-center justify-between text-xs text-slate-500">
          <span>{t.trustCard.systemBox.lastCheck}:</span>
          <span className="text-ag-ink" data-testid="last-check-ts">
            {status?.last_check_at ? new Date(status.last_check_at).toLocaleString(lang) : t.trustCard.trustCheck.never}
          </span>
        </div>
      </div>

      {/* Trust check action card */}
      <div className="ag-card p-7 border-amber-200/60 bg-gradient-to-br from-amber-50/40 to-white">
        <div className="flex items-center gap-2 mb-1">
          <ShieldCheck className="h-4 w-4 text-amber-700" />
          <h2 className="font-serif text-2xl text-ag-ink">{t.trustCard.trustCheck.title}</h2>
        </div>
        <p className="text-sm text-slate-600 mt-1">{t.trustCard.trustCheck.sub}</p>

        <Button
          onClick={runCheck}
          disabled={running}
          data-testid="trust-check-start"
          className="w-full justify-center h-12 mt-5 bg-ag-ink hover:bg-slate-800 rounded-xl"
        >
          {running ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
          {running ? t.trustCard.trustCheck.running : t.trustCard.trustCheck.start}
        </Button>

        {status?.is_demo && (
          <Button
            onClick={clearTestEntries}
            disabled={clearing}
            variant="ghost"
            data-testid="trust-check-clear"
            className="w-full justify-center h-9 mt-2 text-xs text-slate-500 hover:text-ag-ink"
          >
            <Trash2 className="h-3.5 w-3.5 mr-1.5" />
            {t.trustCard.trustCheck.clearTest}
          </Button>
        )}

        {last && (
          <div className="mt-6 pt-5 border-t border-amber-200/40" data-testid="trust-check-result">
            <div className="flex items-center gap-2 mb-3">
              {last.ok
                ? <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                : <AlertTriangle className="h-5 w-5 text-amber-600" />}
              <div className="font-serif text-lg text-ag-ink">
                {last.ok ? t.trustCard.trustCheck.successTitle : t.trustCard.trustCheck.failTitle}
              </div>
            </div>
            {last.device?.name && (
              <div className="text-xs text-slate-500 inline-flex items-center gap-1.5 mb-3">
                <Smartphone className="h-3 w-3" />
                {last.device.name}
              </div>
            )}
            <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">{t.trustCard.trustCheck.checked}</div>
            <ul className="space-y-1.5">
              {last.steps.map((s) => (
                <li key={s.key} className="flex items-center gap-2 text-sm" data-testid={`step-${s.key}`}>
                  <StatusDot ok={s.ok} />
                  <span className="text-slate-700">{lang === "en" ? s.title_en : s.title_de}</span>
                  {s.detail && <span className="text-xs text-slate-400">— {s.detail}</span>}
                </li>
              ))}
            </ul>
            <div className="mt-4 text-xs text-slate-500">
              <span className="font-medium text-ag-ink">{t.trustCard.trustCheck.systemStatus}: </span>
              <span className={last.ok ? "text-emerald-700" : "text-amber-700"}>{last.system_status}</span>
            </div>

            {last.planned_steps?.length > 0 && (
              <div className="mt-4 pt-4 border-t border-slate-100">
                <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">{t.trustCard.trustCheck.plannedTitle}</div>
                <ul className="grid grid-cols-2 gap-x-3 gap-y-1">
                  {last.planned_steps.map((p) => (
                    <li key={p.key} className="flex items-center gap-2 text-xs text-slate-400">
                      <span className="h-1.5 w-1.5 rounded-full bg-slate-300" />
                      {lang === "en" ? p.title_en : p.title_de}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
}
